import React, { useEffect, useRef, useState } from "react"
import { useSelector, useDispatch } from "react-redux"
import { useHistory } from "react-router-dom"
import { makeStyles } from "@material-ui/core/styles"
import CloudUploadIcon from "@material-ui/icons/CloudUpload"
import HomeIcon from "@material-ui/icons/Home"
import BarChartIcon from "@material-ui/icons/BarChart"
import { MobileBottomNav } from "./MobileBottomNav"
import CollectionsIcon from "@material-ui/icons/Collections"
import PhotoLibraryIcon from "@material-ui/icons/PhotoLibrary"
import PublishIcon from "@material-ui/icons/Publish"
import CheckCircleIcon from "@material-ui/icons/CheckCircle"
import WhiteLogo from "../assets/images/logo/white_logo.png"
import { get, size } from "lodash"
import { getLoggedInUserDisplayName } from "../v2_actions/auth"
import { getInitials } from "../v2_actions/photographer"
import { galleryList } from "../v2_actions/gallery"
import { photographList } from "../v2_actions/photograph"
import { processGalleryPhotographsList } from "../v2_actions/process_gallery_photographs"
import { facialRecognitionList } from "../v2_actions/facial_recognition"
import { getUserStoredCountry } from "../v2_actions/auth_country"

const MIN_FILE_SIZE_BYTES = 204800
const MAX_FILE_SIZE_BYTES = 5242880
const ACCEPTED_TYPES = ["image/jpg", "image/jpeg"]

export const MobileUpload = () => {
    const [activeStep, setActiveStep] = useState(0)
    const [selectedGallery, setSelectedGallery] = useState(null)
    const [showGalleryDropdown, setShowGalleryDropdown] = useState(false)
    const [selectedFiles, setSelectedFiles] = useState([])
    const [showWarning, setShowWarning] = useState(false)
    const [showUploadModal, setShowUploadModal] = useState(false)
    const [uploading, setUploading] = useState(false)
    const [uploadProgress, setUploadProgress] = useState({ success: 0, failed: 0, total: 0 })
    const [uploadComplete, setUploadComplete] = useState(false)
    const [uploadError, setUploadError] = useState(null)
    const [fileValidationError, setFileValidationError] = useState(null)
    const fileInputRef = useRef(null)
    const classes = useStyles()
    const dispatch = useDispatch()
    const history = useHistory()
    const displayName = getLoggedInUserDisplayName()
    const initials = getInitials(displayName)
    const galleries = useSelector(() => galleryList.getVisibleObjects())

    const handleStepClick = (index) => {
        if (index === 0) {
            dispatch(galleryList.fetchListIfNeeded())
            setShowGalleryDropdown(!showGalleryDropdown)
        }
        if (index === 1 && activeStep >= 1) {
            setShowWarning(true)
        }
        if (index === 2 && activeStep === 2 && !showUploadModal) {
            setShowUploadModal(true)
        }
    }

    const progressRef = useRef({ success: 0, failed: 0 })

    const startUpload = () => {
        const country = getUserStoredCountry()
        const countryCode = get(country, "country_code")
        const galleryId = selectedGallery.id
        const total = selectedFiles.length

        progressRef.current = { success: 0, failed: 0 }
        setUploading(true)
        setUploadProgress({ success: 0, failed: 0, total })

        const onFileProgress = (file, status) => {
            if (status === "success" || status === "failure") {
                progressRef.current.success++
            }
            setUploadProgress({
                success: progressRef.current.success,
                failed: 0,
                total,
            })
        }

        dispatch(photographList.fetchGalleryPhotographs(galleryId)).then(() => {
            const galleryPhotographs = photographList.getGalleryPhotographs()
            return galleryList.savePhotosv2({
                files: selectedFiles,
                galleryPhotographs: galleryPhotographs,
                galleryId: galleryId,
                countryCode: countryCode,
                on_file_upload_progress: onFileProgress,
            })
        }).then(() => {
            dispatch(processGalleryPhotographsList.processGalleryPhotographs(galleryId))
            dispatch(facialRecognitionList.indexGalleryForFacialRecognition(galleryId))
            dispatch(galleryList.invalidateList())

            setUploading(false)
            setUploadComplete(true)
            setActiveStep(3)
        }).catch((err) => {
            setUploading(false)
            setUploadError("Upload failed: " + (err.message || "Unknown error"))
            setActiveStep(3)
        })
    }

    const handleWarningOk = () => {
        setShowWarning(false)
        fileInputRef.current.click()
    }

    const handleFilesSelected = (e) => {
        const files = Array.from(e.target.files)
        if (files.length === 0) return

        setFileValidationError(null)

        const invalidType = files.filter((f) => !ACCEPTED_TYPES.includes(f.type))
        if (invalidType.length > 0) {
            setFileValidationError("Only JPG/JPEG files are accepted.")
            return
        }

        const tooSmall = files.filter((f) => f.size < MIN_FILE_SIZE_BYTES)
        const tooBig = files.filter((f) => f.size > MAX_FILE_SIZE_BYTES)
        if (tooSmall.length > 0 || tooBig.length > 0) {
            setFileValidationError("Photos must be between 200KB and 5MB.")
            return
        }

        setSelectedFiles(files)
        setActiveStep(2)
    }

    const handleGallerySelect = (gallery) => {
        setSelectedGallery(gallery)
        setShowGalleryDropdown(false)
        setActiveStep(1)
    }

    const steps = [
        { label: "Select Gallery", icon: CollectionsIcon, description: "Choose a gallery to add photos to" },
        { label: "Select Photos", icon: PhotoLibraryIcon, description: "Pick photos from your phone" },
        { label: "Upload Photos", icon: PublishIcon, description: "Upload photos to the gallery" },
    ]

    return (
        <div className={classes.container}>
            <div className={classes.header}>
                <img src={WhiteLogo} alt="bossfotos" className={classes.logo} />
                <div className={classes.avatar}>{initials}</div>
            </div>

            <div className={classes.content}>
                <div className={classes.pageTitle}>Upload Photos</div>

                <div className={classes.stepper}>
                    {steps.map((step, index) => {
                        const StepIcon = step.icon
                        const isActive = index === activeStep
                        const isCompleted = index < activeStep
                        return (
                            <div key={index} className={classes.stepWrapper}>
                                <div className={classes.stepRow} onClick={() => handleStepClick(index)} style={{ cursor: (index <= 1 || (index === 2 && activeStep === 2)) ? "pointer" : "default" }}>
                                    <div className={`${classes.stepCircle} ${isActive ? classes.stepCircleActive : ""} ${isCompleted ? classes.stepCircleCompleted : ""}`}>
                                        {isCompleted ? (
                                            <CheckCircleIcon className={classes.stepCheckIcon} />
                                        ) : (
                                            <StepIcon className={`${classes.stepIcon} ${isActive ? classes.stepIconActive : ""}`} />
                                        )}
                                    </div>
                                    <div className={classes.stepContent}>
                                        <div className={classes.stepNumber}>Step {index + 1}</div>
                                        <div className={`${classes.stepLabel} ${isActive ? classes.stepLabelActive : ""} ${isCompleted ? classes.stepLabelCompleted : ""}`}>
                                            {step.label}
                                        </div>
                                        {index === 0 && selectedGallery ? (
                                            <div className={classes.selectedGalleryName}>{selectedGallery.session_name}</div>
                                        ) : index === 1 && selectedFiles.length > 0 ? (
                                            <div className={classes.selectedGalleryName}>{selectedFiles.length} photos selected</div>
                                        ) : index === 2 && uploading ? (
                                            <div className={classes.uploadProgress}>Uploading {uploadProgress.success} of {uploadProgress.total}...</div>
                                        ) : index === 2 && uploadComplete ? (
                                            <div className={classes.uploadSuccess}>{selectedFiles.length} photos uploaded</div>
                                        ) : index === 2 && uploadError ? (
                                            <div className={classes.uploadError}>{uploadError}</div>
                                        ) : index === 2 && activeStep === 2 ? (
                                            <div className={classes.uploadReady}>Tap to start upload</div>
                                        ) : (
                                            <div className={classes.stepDescription}>{step.description}</div>
                                        )}
                                    </div>
                                </div>
                                {index === 0 && showGalleryDropdown && (
                                    <div className={classes.galleryDropdown}>
                                        {galleries.map((gallery) => (
                                            <div
                                                key={gallery.id}
                                                className={`${classes.galleryDropdownItem} ${selectedGallery && selectedGallery.id === gallery.id ? classes.galleryDropdownItemSelected : ""}`}
                                                onClick={() => handleGallerySelect(gallery)}
                                            >
                                                {gallery.session_name}
                                            </div>
                                        ))}
                                    </div>
                                )}
                                {index < steps.length - 1 && (
                                    <div className={classes.stepConnector}>
                                        <div className={`${classes.stepConnectorLine} ${isCompleted ? classes.stepConnectorLineCompleted : ""}`} />
                                    </div>
                                )}
                            </div>
                        )
                    })}
                </div>

                {showUploadModal && (
                    <div className={classes.uploadStatusCard}>
                        {!uploading && !uploadComplete && !uploadError && (
                            <>
                                <div className={classes.progressTitle}>Ready to Upload</div>
                                <div className={classes.progressText}>
                                    {selectedFiles.length} photos to {selectedGallery.session_name}
                                </div>
                                <div className={classes.doneButton} onClick={startUpload}>Start Upload</div>
                            </>
                        )}
                        {uploading && (
                            <>
                                <div className={classes.progressTitle}>Uploading Photos</div>
                                <div className={classes.progressText}>
                                    {uploadProgress.success} of {uploadProgress.total} photos uploaded
                                </div>
                                <div className={classes.progressBarContainer}>
                                    <div
                                        className={classes.progressBar}
                                        style={{ width: `${uploadProgress.total > 0 ? (uploadProgress.success / uploadProgress.total) * 100 : 0}%` }}
                                    />
                                </div>
                                <div className={classes.progressHint}>Please do not close this page</div>
                            </>
                        )}
                        {uploadComplete && (
                            <>
                                <CheckCircleIcon className={classes.successIcon} />
                                <div className={classes.progressTitle}>Upload Complete</div>
                                <div className={classes.progressText}>
                                    {selectedFiles.length} photos uploaded successfully
                                </div>
                                <div className={classes.doneButton} onClick={() => history.push("/photographer/mobile")}>Done</div>
                            </>
                        )}
                        {uploadError && !uploading && (
                            <>
                                <div className={classes.progressTitle}>Upload Finished</div>
                                <div className={classes.uploadError}>{uploadError}</div>
                                <div className={classes.doneButton} onClick={() => history.push("/photographer/mobile")}>Done</div>
                            </>
                        )}
                    </div>
                )}
            </div>

            <MobileBottomNav activePage="upload" />
            <input
                ref={fileInputRef}
                type="file"

                multiple
                style={{ display: "none" }}
                onChange={handleFilesSelected}
            />

            {showWarning && (
                <div className={classes.overlay}>
                    <div className={classes.warningModal}>
                        <div className={classes.warningTitle}>Warning</div>
                        <div className={classes.warningText}>
                            Make sure you select the original size photographs from your phone.
                        </div>
                        <div className={classes.warningButton} onClick={handleWarningOk}>OK</div>
                    </div>
                </div>
            )}

            {fileValidationError && (
                <div className={classes.overlay}>
                    <div className={classes.warningModal}>
                        <div className={classes.warningTitle}>Invalid Files</div>
                        <div className={classes.warningText}>{fileValidationError}</div>
                        <div className={classes.warningButton} onClick={() => setFileValidationError(null)}>OK</div>
                    </div>
                </div>
            )}

        </div>
    )
}

const useStyles = makeStyles({
    container: {
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
        fontFamily: "Helvetica, Raleway, sans-serif",
        paddingBottom: "60px",
    },
    header: {
        background: "linear-gradient(135deg, #0d2137 0%, #1e88e5 100%)",
        padding: "16px 20px",
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
    },
    logo: {
        height: "36px",
        width: "auto",
    },
    avatar: {
        width: "36px",
        height: "36px",
        borderRadius: "50%",
        backgroundColor: "#1565c0",
        color: "#fff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "13px",
        fontWeight: 600,
        border: "2px solid #fff",
    },
    content: {
        padding: "16px",
        flex: 1,
    },
    pageTitle: {
        fontSize: "14px",
        fontWeight: 800,
        color: "#42a5f5",
        textTransform: "uppercase",
        fontFamily: "Raleway, sans-serif",
        letterSpacing: "0.5px",
        marginBottom: "20px",
    },
    stepper: {
        display: "flex",
        flexDirection: "column",
    },
    stepWrapper: {
        display: "flex",
        flexDirection: "column",
    },
    stepRow: {
        display: "flex",
        alignItems: "flex-start",
        gap: "14px",
    },
    stepCircle: {
        width: "48px",
        height: "48px",
        borderRadius: "50%",
        backgroundColor: "#e0e0e0",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flexShrink: 0,
    },
    stepCircleActive: {
        backgroundColor: "#1e88e5",
    },
    stepCircleCompleted: {
        backgroundColor: "#4caf50",
    },
    stepIcon: {
        fontSize: "24px",
        color: "#999",
    },
    stepIconActive: {
        color: "#fff",
    },
    stepCheckIcon: {
        fontSize: "32px",
        color: "#fff",
    },
    stepContent: {
        flex: 1,
        paddingTop: "2px",
    },
    stepNumber: {
        fontSize: "11px",
        fontWeight: 600,
        color: "#999",
        textTransform: "uppercase",
        letterSpacing: "0.5px",
        marginBottom: "2px",
    },
    stepLabel: {
        fontSize: "15px",
        fontWeight: 700,
        color: "#999",
        marginBottom: "2px",
    },
    stepLabelActive: {
        color: "#333",
    },
    stepLabelCompleted: {
        color: "#4caf50",
    },
    stepDescription: {
        fontSize: "12px",
        color: "#999",
    },
    stepConnector: {
        paddingLeft: "23px",
        height: "24px",
        display: "flex",
        alignItems: "stretch",
    },
    stepConnectorLine: {
        width: "2px",
        backgroundColor: "#e0e0e0",
    },
    stepConnectorLineCompleted: {
        backgroundColor: "#4caf50",
    },
    selectedGalleryName: {
        fontSize: "18px",
        fontWeight: 700,
        color: "#1e88e5",
        marginTop: "4px",
    },
    uploadProgress: {
        fontSize: "16px",
        fontWeight: 700,
        color: "#1e88e5",
        marginTop: "4px",
    },
    uploadSuccess: {
        fontSize: "18px",
        fontWeight: 700,
        color: "#4caf50",
        marginTop: "4px",
    },
    uploadError: {
        fontSize: "16px",
        fontWeight: 700,
        color: "#f44336",
        marginTop: "4px",
    },
    uploadReady: {
        fontSize: "16px",
        fontWeight: 700,
        color: "#1e88e5",
        marginTop: "4px",
    },
    galleryDropdown: {
        marginLeft: "62px",
        marginTop: "8px",
        marginBottom: "8px",
        backgroundColor: "#fff",
        borderRadius: "12px",
        boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        maxHeight: "200px",
        overflowY: "auto",
    },
    galleryDropdownItem: {
        padding: "12px 16px",
        fontSize: "14px",
        fontWeight: 500,
        color: "#333",
        cursor: "pointer",
        borderBottom: "1px solid #f0f0f0",
        "&:last-child": {
            borderBottom: "none",
        },
        "&:active": {
            backgroundColor: "#e3f2fd",
        },
    },
    galleryDropdownItemSelected: {
        color: "#1e88e5",
        fontWeight: 600,
        backgroundColor: "#e3f2fd",
    },
    overlay: {
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: "rgba(0,0,0,0.5)",
        zIndex: 2000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "16px",
    },
    warningModal: {
        backgroundColor: "#fff",
        borderRadius: "16px",
        padding: "24px",
        width: "100%",
        maxWidth: "320px",
        textAlign: "center",
    },
    warningTitle: {
        fontSize: "18px",
        fontWeight: 700,
        color: "#333",
        marginBottom: "12px",
    },
    warningText: {
        fontSize: "14px",
        color: "#666",
        lineHeight: 1.5,
        marginBottom: "20px",
    },
    progressIcon: {
        fontSize: "48px",
        color: "#1e88e5",
        marginBottom: "16px",
    },
    successIcon: {
        fontSize: "48px",
        color: "#4caf50",
        marginBottom: "16px",
    },
    progressTitle: {
        fontSize: "18px",
        fontWeight: 700,
        color: "#333",
        marginBottom: "8px",
    },
    progressText: {
        fontSize: "14px",
        color: "#666",
        marginBottom: "16px",
    },
    progressBarContainer: {
        width: "100%",
        height: "8px",
        backgroundColor: "#e0e0e0",
        borderRadius: "4px",
        overflow: "hidden",
        marginBottom: "12px",
    },
    progressBar: {
        height: "100%",
        backgroundColor: "#1e88e5",
        borderRadius: "4px",
        transition: "width 0.3s ease",
    },
    progressHint: {
        fontSize: "12px",
        color: "#999",
    },
    warningButton: {
        backgroundColor: "#1e88e5",
        color: "#fff",
        fontSize: "14px",
        fontWeight: 600,
        padding: "12px",
        borderRadius: "10px",
        cursor: "pointer",
    },
    uploadStatusCard: {
        marginTop: "24px",
        backgroundColor: "#fff",
        borderRadius: "12px",
        padding: "24px 16px",
        boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
        textAlign: "center",
    },
    doneButton: {
        backgroundColor: "#1e88e5",
        color: "#fff",
        fontSize: "14px",
        fontWeight: 600,
        padding: "12px",
        borderRadius: "10px",
        cursor: "pointer",
        marginTop: "12px",
    },
    bottomNav: {
        backgroundColor: "#fff",
        borderTop: "1px solid #e0e0e0",
        padding: "8px 0",
        display: "flex",
        justifyContent: "space-around",
        alignItems: "center",
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 1000,
    },
    navItem: {
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        cursor: "pointer",
        padding: "4px 12px",
    },
    navIcon: {
        fontSize: "24px",
        color: "#0d2137",
        marginBottom: "2px",
    },
    navIconActive: {
        fontSize: "24px",
        color: "#1e88e5",
        marginBottom: "2px",
    },
    navLabel: {
        fontSize: "10px",
        color: "#0d2137",
    },
    navLabelActive: {
        fontSize: "10px",
        color: "#1e88e5",
        fontWeight: 600,
    },
})
